/**
 * «Главная» клиента (client-home-dashboard): чистые хелперы сводки — текст алерта «Требует
 * внимания», список «Ближайшие сроки» и подписи строк. Источник данных — тот же журнал сроков,
 * что и в разделе «Обучение» (`GET /api/training/journal`): статусы и счётчики считает сервер,
 * здесь они только отбираются и подписываются, поэтому «Главная» не расходится с журналом.
 * Сроки спецоценки (home-sout-deadlines) — так же из данных раздела «Спецоценка»
 * (`GET /api/sout/dashboard`): статус рабочего места и счётчики считает сервер.
 */
import type { JournalRow, SoutDashboardRow, TrainingRecord } from '@/lib/api'
import { pluralRu } from '@/lib/orders'
import { shortTerm } from '@/lib/training'

/** Сколько строк показывает каждый блок «Главной» — остальное по ссылке в раздел. */
export const HOME_DEADLINES_LIMIT = 5
/** Спецоценка в «Ближайших сроках» — отдельным коротким списком под обучением. */
export const HOME_SOUT_DEADLINES_LIMIT = 3
export const HOME_ORDERS_LIMIT = 4
export const HOME_DOCUMENTS_LIMIT = 4

const MONTH_DAYS = 30

/**
 * Период порога «истекает скоро» словами: «в ближайшие 3 месяца» (90 дней), «в ближайший месяц»
 * (30), иначе в днях — «в ближайшие 45 дней». Порог приходит из журнала (`threshold`), отдельного
 * порога «Главная» не вводит.
 */
export function thresholdPhrase(thresholdDays: number): string {
  const months = thresholdDays / MONTH_DAYS
  const [n, one, few, many] =
    thresholdDays > 0 && Number.isInteger(months)
      ? [months, 'месяц', 'месяца', 'месяцев']
      : [thresholdDays, 'день', 'дня', 'дней']
  const nearest = pluralRu(n, 'ближайший', 'ближайшие', 'ближайшие')
  return `в ${nearest} ${n === 1 ? '' : `${n} `}${pluralRu(n, one, few, many)}`
}

export interface AttentionInput {
  /** Счётчики журнала: люди с просроченным обучением и с истекающим в пределах порога. */
  overdue: number
  soon: number
  /** Порог «истекает скоро» в днях (`JournalResponse.threshold`). */
  threshold: number
  /** Клиент-физлицо: в журнале только он сам, «сотрудников» в тексте быть не должно. */
  individual: boolean
}

/**
 * Текст алерта «Требует внимания» либо `null`, когда внимания ничего не требует (ни просроченных,
 * ни истекающих) — тогда алерт не показывается вовсе.
 */
export function attentionText({
  overdue,
  soon,
  threshold,
  individual,
}: AttentionInput): string | null {
  if (overdue <= 0 && soon <= 0) return null
  const period = thresholdPhrase(threshold)
  if (individual) {
    return overdue > 0 ? 'Срок вашего обучения истёк' : `Срок вашего обучения истекает ${period}`
  }
  if (overdue <= 0) {
    return `У ${soon} ${pluralRu(soon, 'сотрудника', 'сотрудников', 'сотрудников')} обучение истекает ${period}`
  }
  const overduePart = `${overdue} ${pluralRu(overdue, 'сотрудник', 'сотрудника', 'сотрудников')} с просроченным обучением`
  return soon > 0 ? `${overduePart} · ${soon} — истекает ${period}` : overduePart
}

export interface SoutAttentionInput {
  /** Счётчики раздела «Спецоценка»: рабочие места с истёкшей и с истекающей спецоценкой. */
  overdue: number
  soon: number
  /** Порог «истекает скоро» в днях (`SoutDashboard.threshold`). */
  threshold: number
}

/**
 * Строка алерта «Требует внимания» про спецоценку либо `null`, когда по ней внимания ничего не
 * требует. Считаются рабочие места (а не сотрудники, как у обучения) — единица названа в тексте.
 */
export function soutAttentionText({ overdue, soon, threshold }: SoutAttentionInput): string | null {
  if (overdue <= 0 && soon <= 0) return null
  const period = thresholdPhrase(threshold)
  if (overdue <= 0) {
    return `У ${soon} ${pluralRu(soon, 'рабочего места', 'рабочих мест', 'рабочих мест')} спецоценка истекает ${period}`
  }
  const overduePart = `${overdue} ${pluralRu(overdue, 'рабочее место', 'рабочих места', 'рабочих мест')} с просроченной спецоценкой`
  return soon > 0 ? `${overduePart} · ${soon} — истекает ${period}` : overduePart
}

/** Строка блока «Ближайшие сроки»: сотрудник + программа с просроченным/истекающим обучением. */
export interface UrgentDeadline {
  key: string
  personId: string
  fullName: string
  directionName: string
  /** Программа обучения — по ней у сотрудника свой срок; отличает строки одного направления. */
  programName: string
  status: 'expired' | 'expiring'
  /** Срок действия обучения (YYYY-MM-DD); по нему строки и упорядочены. */
  dueOn?: string
  /** Подпись цветной метки: «Просрочено», «Осталось N дн.» либо «Истекает сегодня». */
  label: string
}

type UrgentRecord = TrainingRecord & { status: 'expired' | 'expiring' }

function isUrgent(record: TrainingRecord): record is UrgentRecord {
  return record.status === 'expired' || record.status === 'expiring'
}

/**
 * Порядок срочности: сначала просроченные, затем истекающие; внутри — по сроку (раньше = срочнее,
 * запись без срока — в конце). Даты YYYY-MM-DD сравниваются как строки.
 */
function compareUrgency(
  a: { status: 'expired' | 'expiring'; dueOn?: string },
  b: { status: 'expired' | 'expiring'; dueOn?: string },
): number {
  if (a.status !== b.status) return a.status === 'expired' ? -1 : 1
  if (a.dueOn === b.dueOn) return 0
  if (!a.dueOn) return 1
  if (!b.dueOn) return -1
  return a.dueOn < b.dueOn ? -1 : 1
}

/**
 * Подпись истекающего обучения: «Осталось N дн.» — N тот же, что в журнале сроков (`shortTerm`,
 * зеркало серверного расчёта); в последний день — «Истекает сегодня». Если срок в днях не
 * выражается (нет даты окончания), остаётся название статуса.
 */
function expiringLabel(
  record: { dueOn?: string | null; recurrenceMode?: TrainingRecord['recurrenceMode'] },
  now: Date,
): string {
  const term = shortTerm(
    { status: 'expiring', dueOn: record.dueOn, recurrenceMode: record.recurrenceMode },
    now,
  )
  const days = /^(\d+) дн\.$/.exec(term)
  if (!days) return 'Истекает скоро'
  return days[1] === '0' ? 'Истекает сегодня' : `Осталось ${term}`
}

/**
 * Самые срочные сроки обучения для блока «Ближайшие сроки». Берутся записи журнала со статусом
 * «просрочено»/«истекает скоро» (их статус считает сервер — тот же, что красит строку журнала).
 * Каждая программа сотрудника — отдельная строка: у программ одного направления сроки разные, и
 * просрочка по одной не должна скрывать приближающийся срок по другой (журнал уже отдаёт по одной
 * записи на программу — последнее обучение). Результат упорядочен по срочности и обрезан до `limit`.
 */
export function urgentDeadlines(
  rows: JournalRow[],
  limit: number = HOME_DEADLINES_LIMIT,
  now: Date = new Date(),
): UrgentDeadline[] {
  const deadlines: UrgentDeadline[] = []
  for (const row of rows) {
    for (const record of row.records) {
      if (!isUrgent(record)) continue
      deadlines.push({
        key: record.id,
        personId: row.personId,
        fullName: row.fullName,
        directionName: record.directionName,
        programName: record.programName,
        status: record.status,
        dueOn: record.dueOn,
        label: record.status === 'expired' ? 'Просрочено' : expiringLabel(record, now),
      })
    }
  }
  return deadlines
    .sort(
      (a, b) =>
        compareUrgency(a, b) ||
        a.fullName.localeCompare(b.fullName, 'ru') ||
        a.directionName.localeCompare(b.directionName, 'ru') ||
        a.programName.localeCompare(b.programName, 'ru'),
    )
    .slice(0, limit)
}

/** Строка «Ближайших сроков» по спецоценке: рабочее место с истёкшим/истекающим сроком СОУТ. */
export interface UrgentSoutDeadline {
  key: string
  workplaceId: string
  /** Должность — название рабочего места, как в таблице раздела «Спецоценка». */
  position: string
  departmentName: string
  status: 'expired' | 'expiring'
  /** Срок действия спецоценки (YYYY-MM-DD). */
  dueOn?: string
  /** Подпись цветной метки — те же формулировки, что у обучения. */
  label: string
}

/**
 * Самые срочные сроки спецоценки: рабочие места со статусом «просрочено»/«истекает скоро» (статус
 * считает сервер — тот же, что в таблице раздела «Спецоценка»). Порядок — как у обучения: сначала
 * просроченные, затем истекающие, внутри — по сроку; при равенстве — по подразделению и должности.
 */
export function urgentSoutDeadlines(
  rows: SoutDashboardRow[],
  limit: number = HOME_SOUT_DEADLINES_LIMIT,
  now: Date = new Date(),
): UrgentSoutDeadline[] {
  const deadlines: UrgentSoutDeadline[] = []
  for (const row of rows) {
    if (row.status !== 'overdue' && row.status !== 'soon') continue
    const expired = row.status === 'overdue'
    deadlines.push({
      key: row.workplaceId,
      workplaceId: row.workplaceId,
      position: row.position,
      departmentName: row.departmentName,
      status: expired ? 'expired' : 'expiring',
      dueOn: row.validUntil,
      label: expired ? 'Просрочено' : expiringLabel({ dueOn: row.validUntil }, now),
    })
  }
  return deadlines
    .sort(
      (a, b) =>
        compareUrgency(a, b) ||
        a.departmentName.localeCompare(b.departmentName, 'ru') ||
        a.position.localeCompare(b.position, 'ru'),
    )
    .slice(0, limit)
}

/**
 * Дата «ДД.ММ.ГГГГ». Дата-только (YYYY-MM-DD: срок обучения, дата документа) форматируется без
 * `Date` — иначе в поясах западнее UTC получился бы предыдущий день; отметка времени (дата
 * создания заказа) — по местному времени, как в разделе «Мои заказы».
 */
export function formatHomeDate(iso: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (dateOnly) return `${dateOnly[3]}.${dateOnly[2]}.${dateOnly[1]}`
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Подпись срока строки «Ближайшие сроки»: «срок истёк 01.10.2026» / «до 10.10.2026». */
export function deadlineDateLabel(
  deadline: Pick<UrgentDeadline, 'status' | 'dueOn'>,
): string | null {
  if (!deadline.dueOn) return null
  const date = formatHomeDate(deadline.dueOn)
  return deadline.status === 'expired' ? `срок истёк ${date}` : `до ${date}`
}

/**
 * Тип файла по расширению названия документа («Отчёт.pdf» → «PDF»); `null`, если расширения нет —
 * тогда строка показывает тип документа из реестра.
 */
export function fileTypeLabel(title: string): string | null {
  const ext = /\.([A-Za-z0-9]{1,5})$/.exec(title.trim())
  return ext ? ext[1].toUpperCase() : null
}
